import { useEffect, useRef, useState } from 'react';
import { eventPush } from '@_shared/libs';
import { Typography } from '@_shared/ui/Typography';

import * as S from './Editor.style';
import { TextArea } from './TextArea';

interface IEditorProps {
	placeholder: string;
	defaultValue: string;
	onSubmit: (data: string) => void;
	maxLength: number;
	minLength: number;
	title: string;
	eventPushName: string;
	children: React.ReactNode;
}

export function Editor({
	defaultValue,
	onSubmit,
	placeholder,
	maxLength,
	minLength,
	title,
	eventPushName,
	children,
}: IEditorProps) {
	const [data, setData] = useState<string>(defaultValue);
	const [isEditMode, setIsEditMode] = useState(false);

	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const autoResizeTextarea = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = 'auto';
			const height = textareaRef.current.scrollHeight; // 높이
			textareaRef.current.style.height = `${height + 8}px`;
		}
	};

	useEffect(() => {
		if (isEditMode && textareaRef.current) {
			textareaRef.current.focus();
		}
	}, [isEditMode]);

	return (
		<>
			<S.HeadlineRow>
				<Typography type="headline-small" color="brown-primary">
					{title}
				</Typography>
				<S.MemoTextContainer>
					{isEditMode ? (
						<>
							<S.MemoTextWrapper
								onClick={() => {
									setIsEditMode((prev) => !prev);
								}}
							>
								<Typography type="title-small" color="gray-secondary">
									취소
								</Typography>
							</S.MemoTextWrapper>
							<S.MemoTextWrapper>
								<Typography type="title-small" color="gray-secondary">
									|
								</Typography>
							</S.MemoTextWrapper>
							<S.MemoTextWrapper
								onClick={() => {
									if (data === defaultValue) {
										setIsEditMode(false);
									} else {
										onSubmit(data);
									}
								}}
							>
								<Typography
									type="title-small"
									color={
										data === defaultValue ? 'gray-secondary' : 'orange-primary'
									}
								>
									저장
								</Typography>
							</S.MemoTextWrapper>
						</>
					) : (
						<S.MemoTextWrapper
							onClick={() => {
								eventPush(eventPushName);
								setIsEditMode((prev) => !prev);
							}}
						>
							<Typography type="title-small" color="orange-primary">
								수정
							</Typography>
						</S.MemoTextWrapper>
					)}
				</S.MemoTextContainer>
			</S.HeadlineRow>
			{isEditMode ? (
				<TextArea
					ref={textareaRef}
					placeholder={placeholder}
					defaultValue={defaultValue}
					maxLength={maxLength}
					minLength={minLength}
					onInput={(e) => {
						setData(e.currentTarget.value);
					}}
					onKeyDown={autoResizeTextarea}
					onKeyUp={autoResizeTextarea}
					onFocus={autoResizeTextarea}
				/>
			) : (
				<S.ContentMemoText>{children}</S.ContentMemoText>
			)}
		</>
	);
}
